import Link from '@/components/link-component'
import Routes from '@/constants/routes'
import { useTranslations, useLocale, localizedPath } from '@/utils/locale'
import { usePage, router } from '@inertiajs/react'
import { useLocalStorage } from 'usehooks-ts'
import { getPdfPath } from '@/utils/print'
import { useState } from 'react'
import { extractSlugFromUrl, copyPageAsMarkdown, getMarkdownUrl } from '@/components/ui/copy-page-dropdown'


export default function HelpUsefulLinks(props: { className?: string }) {
  const { t } = useTranslations()
  const { currentLocale } = useLocale()
  const { url } = usePage()
  const pdfPath = getPdfPath(url)
  const [copied, setCopied] = useState(false)
  const [, setSmartSearchQuery] = useLocalStorage<string | undefined>('smartSearchQuery', undefined)

  // Extract slug for building URLs
  const slug = extractSlugFromUrl(url)
  const markdownUrl = getMarkdownUrl(url, currentLocale)

  const handleCopyMarkdown = async () => {
    const success = await copyPageAsMarkdown(url, currentLocale)
    if (success) {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const openAIChat = () => {
    if (!slug) return
    const fullMarkdownUrl = `${Routes.hostname}${localizedPath(`/help/${slug}.md`, currentLocale)}`
    const prompt = t('Read from ${url} so I can ask questions about it.').replace('${url}', fullMarkdownUrl)
    setSmartSearchQuery(prompt)
    router.visit(localizedPath('/smart-search/', currentLocale))
  }

  // Only show copy actions on help article pages
  const isHelpArticle = slug

  const copyActions = isHelpArticle ? [
    {
      action: handleCopyMarkdown,
      text: copied ? t('Copied!') : t('Copy for LLM'),
      unicode: '\ue905'
    },
    {
      action: openAIChat,
      text: t('Open AI Chat'),
      unicode: '\ue990'
    },
    {
      link: markdownUrl,
      text: t('Open page as Markdown'),
      unicode: '\ue94F',
      external: true
    },
    {
      link: pdfPath,
      text: t('Download as PDF'),
      unicode: '\ue94e',
      external: true
    },
  ] : []

  const usefulLinks = [
    { link: localizedPath('/download/', currentLocale), text: t('Download Bitwarden'), unicode: '\ue928' },
    {
      link: 'https://community.bitwarden.com/t/about-the-feature-requests-category',
      text: t('Feature Request'),
      unicode: '\ue965',
    },
    {
      link: 'https://community.bitwarden.com/c/support/',
      text: t('Ask the Community'),
      unicode: '\ue963',
    },
    {
      link: localizedPath('/learning/', currentLocale),
      text: t('Learning Center'),
      unicode: '\ue963',
    },

    {
      link: Routes.contributorGuidelines,
      text: t('Contributor Guidelines'),
      unicode: '\ue934',
    },
  ]
  return (
    <ul className={`list-none ${props?.className}`}>
      {usefulLinks.map((link, i) => (
        <li className="py-1" key={`h-useful-${i}`}>
          <Link to={link.link} className="flex items-center gap-2">
            <span className="font-bwi text-sm">{link.unicode}</span>{' '}
            <span className="md:text-sm lg:text-base">{link.text}</span>
          </Link>
        </li>
      ))}

      {/* Divider before copy actions */}
      {copyActions.length > 0 && (
        <li className="md:hidden" >
          <div className="border-t border-1 border-secondary-500 w-full my-4" />
        </li>
      )}

      {/* Copy actions for help articles */}
      {copyActions.map((item, i) => (
        <li className="py-1 md:hidden" key={`copy-action-${i}`}>
            {item.action ? (
              <button
                onClick={item.action}
                className="flex items-center gap-2 w-full"
              >
                <span className="flex items-end flex-row-reverse w-full">
                  <span className="font-bwi ml-2 mr-0 text-sm">{item.unicode}</span>
                  <span className="md:text-sm lg:text-base">{item.text}</span>
                </span>
              </button>
            ) : (
              <Link
                to={item.link}
                className="flex items-center gap-2"
                {...(item.external && { target: '_blank', rel: 'noopener noreferrer' })}
              >
                <span className="flex items-end flex-row-reverse">
                  <span className="font-bwi ml-2 mr-0 text-sm">{item.unicode}</span>
                  <span className="md:text-sm lg:text-base">{item.text}</span>
                </span>
              </Link>
            )}
        </li>
      ))}
    </ul>
  )
}
